import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { getOrCreateOrigin, positionOriginAtElement } from '../../helpers/uiHelpers';

// Delegated global tooltip manager. Mount once at app root to handle any
// element with `data-tooltip` or `title`.
export default function Tooltip() {
  const [visible, setVisible] = useState(false);
  const [text, setText] = useState('');
  const [placement, setPlacement] = useState('top');
  const [wrapperStyle] = useState({});
  const tooltipRef = useRef(null);
  const targetRef = useRef(null);
  const showTimeoutRef = useRef(null);
  const originRef = useRef(null);

  useEffect(() => {
    const resolveTooltipTarget = (target) => {
      const blockedByChild = target?.closest && target.closest('[data-tooltip-block-parent]');
      if (blockedByChild && !blockedByChild.hasAttribute('data-tooltip') && !blockedByChild.hasAttribute('title')) {
        return null;
      }
      return target?.closest && target.closest('[data-tooltip], [title]');
    };

    const show = (el) => {
      if (!el) return;
      const tip = el.getAttribute('data-tooltip') || el.getAttribute('title');
      if (!tip) return;
      const place = (el.getAttribute('data-tooltip-placement') || 'top').trim();
      // Cancel any pending hide/show
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
        showTimeoutRef.current = null;
      }
      targetRef.current = el;
      setText(tip);
      setPlacement(place);
      // Delay showing the tooltip by 1000ms
      showTimeoutRef.current = setTimeout(() => {
        showTimeoutRef.current = null;
        setVisible(true);
      }, 1000);
    };

    const hide = () => {
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
        showTimeoutRef.current = null;
      }
      setVisible(false);
      targetRef.current = null;
      setText('');
    };

    const mouseOver = (e) => {
      const el = resolveTooltipTarget(e.target);
      if (el) show(el);
      else hide();
    };
    const mouseOut = (e) => {
      const related = e.relatedTarget;
      if (!related || (targetRef.current && !targetRef.current.contains(related))) hide();
    };
    const focusIn = (e) => {
      const el = resolveTooltipTarget(e.target);
      if (el) show(el);
      else hide();
    };
    const focusOut = () => hide();

    document.addEventListener('mouseover', mouseOver);
    document.addEventListener('mouseout', mouseOut);
    document.addEventListener('focusin', focusIn);
    document.addEventListener('focusout', focusOut);
    // Touch handlers: show on touchstart, hide on touchend/cancel
    const touchStart = (e) => {
      const el = resolveTooltipTarget(e.target);
      if (el) show(el);
      else hide();
    };
    const touchEnd = () => hide();
    document.addEventListener('touchstart', touchStart, { passive: true });
    document.addEventListener('touchend', touchEnd, { passive: true });
    document.addEventListener('touchcancel', touchEnd, { passive: true });

    return () => {
      document.removeEventListener('mouseover', mouseOver);
      document.removeEventListener('mouseout', mouseOut);
      document.removeEventListener('focusin', focusIn);
      document.removeEventListener('focusout', focusOut);
      document.removeEventListener('touchstart', touchStart);
      document.removeEventListener('touchend', touchEnd);
      document.removeEventListener('touchcancel', touchEnd);
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
        showTimeoutRef.current = null;
      }
    };
  }, []);

  // helper to parse placement into vertical/horizontal tokens for classnames
  const parsePlacement = (p) => {
    const normalized = (p || 'top').toLowerCase().trim().replace(/\s+/g, '-');
    const parts = normalized.split('-');
    let vertical = 'top';
    let horizontal = 'center';
    if (parts.length === 1) {
      if (['top', 'bottom', 'left', 'right', 'center'].includes(parts[0])) {
        if (parts[0] === 'left' || parts[0] === 'right') { vertical = 'center'; horizontal = parts[0]; }
        else if (parts[0] === 'center') { vertical = 'center'; horizontal = 'center'; }
        else vertical = parts[0];
      }
    } else {
      const [a, b] = parts;
      const isVertA = ['top', 'bottom', 'center'].includes(a);
      const isVertB = ['top', 'bottom', 'center'].includes(b);
      if (isVertA) { vertical = a; horizontal = isVertB ? 'center' : (b || 'center'); }
      else if (isVertB) { vertical = b; horizontal = a; }
      else { vertical = a; horizontal = b; }
    }
    return { vertical, horizontal };
  };

  // Recompute position after tooltip is rendered so we can measure its size.
  useLayoutEffect(() => {
    if (!visible || !targetRef.current) return;

    // ensure we have a shared origin element and position it at the target
    try {
      originRef.current = getOrCreateOrigin({ className: 'tooltip-origin', preferSibling: '.tooltip-origin' });
      positionOriginAtElement(originRef.current, targetRef.current);
    } catch (err) {}

    const handler = () => {
      if (!targetRef.current || !originRef.current) return;
      positionOriginAtElement(originRef.current, targetRef.current);
    };
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [visible, text, placement]);

  const { vertical, horizontal } = parsePlacement(placement);

  // Render tooltip into the shared origin element when available
  if (typeof document !== 'undefined' && originRef.current) {
    return createPortal(
      <div ref={tooltipRef} className={`tooltip ${visible ? 'show' : ''} ${vertical} ${horizontal}`} aria-hidden={!visible}>
        {text}
      </div>,
      originRef.current
    );
  }

  // Fallback: render inline origin (should be rare)
  return (
    <>
      <div className={`tooltip-origin ${visible ? 'show' : ''}`} style={wrapperStyle} aria-hidden={!visible}>
        <div ref={tooltipRef} className={`tooltip ${visible ? 'show' : ''} ${vertical} ${horizontal}`}>
          {text}
        </div>
      </div>
    </>
  );
}
